import { useEffect, useState, type ReactNode } from "react";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getApiErrorMessage } from "@penn/web-common";
import {
  Plus,
  Trash2,
  Settings2,
  DollarSign,
  SlidersHorizontal,
  FunctionSquare,
  Filter,
  KeyRound,
} from "lucide-react";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import {
  PageHeader,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  AddButton,
} from "@/components/admin/shared/PageHeader";
import { PennField, PennInput, PennDropdown } from "@/components/ui";
import type {
  CostModelFormData} from "@/types/admin/costModel.types";
import {
  costModelSchema,
  PRICE_RULE_SOURCES,
  type PriceRuleSourceValue,
} from "@/types/admin/costModel.types";
import {
  useCostModel,
  useCostModels,
  useCreateCostModel,
  useUpdateCostModel,
} from "@/hooks/useCostModels";
import type {
  CostModelDetail,
  CostModelSummary,
  CreateCostModelBody,
  UpdateCostModelBody,
} from "@/api/costModels";

// Human labels + which conditional sub-section each price source needs.
// Mirrors cost-models.json `priceRule`: avg_base_machine reads the machine_options
// filter (category/subCategory); sherex_price_list_min reads the pricing keys;
// the other two are self-contained (config sum / base + robot adder).
const PRICE_RULE_SOURCE_LABELS: Record<PriceRuleSourceValue, string> = {
  avg_base_machine: "Average base machine price (machine_options filter)",
  sherex_price_list_min: "Sherex price list — minimum (pricing keys)",
  profil_indi_config_sum: "Profil InDie — configured options sum",
  mds_a240_base_plus_robot: "MDS A240 — base machine + robot adder",
};

const PRICE_RULE_SOURCE_OPTIONS = PRICE_RULE_SOURCES.map((v) => ({
  value: v,
  label: PRICE_RULE_SOURCE_LABELS[v],
}));

const usesFilter = (s: PriceRuleSourceValue) => s === "avg_base_machine";
const usesKeys = (s: PriceRuleSourceValue) => s === "sherex_price_list_min";

type ModalMode = "edit" | "view" | "create";
type ModalState = { mode: ModalMode; id?: string } | null;

const dash = (v: string | null | undefined) => (v == null || v === "" ? "—" : v);

const blank: CostModelFormData = {
  modelId: "",
  appliesToTargetType: "",
  appliesToTargetId: "",
  appliesToRegion: "",
  currency: "",
  additionalInvestmentCurrency: "",
  label: "",
  additionalInvestment: "",
  priceRuleSource: "avg_base_machine",
  constants: [],
  formulas: [],
  priceRuleFilters: [],
  priceRuleKeys: [],
};

/** svc-10 detail -> editable form shape (strings for inputs). */
const toForm = (d: CostModelDetail): CostModelFormData => ({
  modelId: d.modelId,
  appliesToTargetType: d.appliesToTargetType ?? "",
  appliesToTargetId: d.appliesToTargetId ?? "",
  appliesToRegion: d.appliesToRegion ?? "",
  currency: d.currency ?? "",
  additionalInvestmentCurrency: d.additionalInvestmentCurrency ?? "",
  label: d.label ?? "",
  additionalInvestment: d.additionalInvestment ?? "",
  priceRuleSource: (d.priceRuleSource ?? "avg_base_machine") as PriceRuleSourceValue,
  constants: d.constants.map((c) => ({
    constantKey: c.constantKey,
    value: c.value ?? "",
  })),
  formulas: d.formulas.map((f) => ({
    formulaKey: f.formulaKey,
    expression: f.expression ?? "",
    evalOrder: f.evalOrder,
  })),
  priceRuleFilters: d.priceRuleFilters.map((f) => ({
    filterCategory: f.filterCategory,
    filterSubcategory: f.filterSubcategory,
  })),
  priceRuleKeys: d.priceRuleKeys.map((k) => ({
    sherexKey: k.sherexKey,
    sortOrder: k.sortOrder == null ? "" : String(k.sortOrder),
  })),
});

/**
 * Editable form shape -> svc-10 PUT body. The price-rule sub-config is kept
 * consistent with the chosen source: only the relevant array is sent and the
 * other is cleared (avg_base_machine -> filters only; sherex -> keys only;
 * profil/mds -> neither). This mirrors the cost-models.json `priceRule` shape.
 */
const toBody = (data: CostModelFormData): UpdateCostModelBody => {
  const source = data.priceRuleSource;
  return {
    label: data.label.trim() === "" ? null : data.label.trim(),
    additionalInvestment:
      data.additionalInvestment.trim() === "" ? null : data.additionalInvestment.trim(),
    priceRuleSource: source,
    constants: data.constants.map((c) => ({
      constantKey: c.constantKey.trim(),
      value: c.value.trim() === "" ? null : c.value.trim(),
    })),
    formulas: data.formulas.map((f) => ({
      formulaKey: f.formulaKey.trim(),
      expression: f.expression.trim() === "" ? null : f.expression,
      evalOrder: f.evalOrder,
    })),
    priceRuleFilters: usesFilter(source)
      ? data.priceRuleFilters.map((f) => ({
          filterCategory: f.filterCategory.trim(),
          filterSubcategory: f.filterSubcategory.trim(),
        }))
      : [],
    priceRuleKeys: usesKeys(source)
      ? data.priceRuleKeys.map((k) => ({
          sherexKey: k.sherexKey.trim(),
          sortOrder: k.sortOrder.trim() === "" ? null : Number(k.sortOrder),
        }))
      : [],
  };
};

/** Create body = the editable writable set + modelId + descriptive identity. */
const toCreateBody = (data: CostModelFormData): CreateCostModelBody => ({
  modelId: data.modelId.trim(),
  ...toBody(data),
  appliesToTargetType: data.appliesToTargetType.trim() || null,
  appliesToTargetId: data.appliesToTargetId.trim() || null,
  appliesToRegion: data.appliesToRegion.trim() || null,
  currency: data.currency.trim() || null,
  additionalInvestmentCurrency: data.additionalInvestmentCurrency.trim() || null,
});

const MODEL_ID_RE = /^[a-z0-9_]{1,60}$/i;

const Section = ({ icon, title, action, children }: { icon: ReactNode; title: string; action?: ReactNode; children: ReactNode }) => (
  <div className="border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden bg-white dark:bg-gray-900">
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
      <span className="text-blue_grey3">{icon}</span>
      <p className="uppercase text-blue_grey3 text-small-medium">{title}</p>
      {action ? <span className="ml-auto">{action}</span> : null}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const ReadOnly = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-blue_grey3 mb-1 text-small-medium">{label}</p>
    <p className="font-mono text-blue_grey px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800 truncate text-body">
      {value}
    </p>
  </div>
);

const addBtnClass =
  "inline-flex items-center gap-2 px-3 py-2 text-body text-penn-500 dark:text-penn-400 hover:bg-penn-100 dark:hover:bg-penn-900/40 rounded-lg transition-colors";

const removeBtnClass =
  "inline-flex items-center justify-center w-10 h-[42px] rounded-2xl text-blue_grey4 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors";

export const CostModelsPage = () => {
  const { data: list, isLoading, isError, error } = useCostModels();
  const items = list ?? [];

  const [modal, setModal] = useState<ModalState>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const detailQuery = useCostModel(modal?.id);
  const detail = detailQuery.data;
  const updateModel = useUpdateCostModel();
  const createModel = useCreateCostModel();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CostModelFormData>({
    resolver: zodResolver(costModelSchema),
    defaultValues: blank,
  });

  const constants = useFieldArray({ control, name: "constants" });
  const formulas = useFieldArray({ control, name: "formulas" });
  const filters = useFieldArray({ control, name: "priceRuleFilters" });
  const keys = useFieldArray({ control, name: "priceRuleKeys" });

  // Drives the conditional price-rule sub-sections (filter vs keys vs none).
  const watchedSource =
    (useWatch({ control, name: "priceRuleSource" }) as
      | PriceRuleSourceValue
      | undefined) ?? "avg_base_machine";

  useEffect(() => {
    if (modal?.id && detail) reset(toForm(detail));
  }, [modal?.id, detail, reset]);

  const openCreate = () => {
    setSaveError(null);
    reset(blank);
    setModal({ mode: "create" });
  };
  const openEdit = (row: CostModelSummary) => {
    setSaveError(null);
    reset(blank);
    setModal({ mode: "edit", id: row.modelId });
  };
  const openView = (row: CostModelSummary) => {
    setSaveError(null);
    reset(blank);
    setModal({ mode: "view", id: row.modelId });
  };
  const closeModal = () => setModal(null);

  const onSubmit = (data: CostModelFormData) => {
    if (!modal) return;
    setSaveError(null);

    // Consistency guard: the chosen source must have its required sub-config so
    // the engine's resolver has something to price from.
    if (usesFilter(data.priceRuleSource) && data.priceRuleFilters.length === 0) {
      setSaveError(
        "Average base machine pricing needs at least one Price Rule Filter (e.g. Machine / Machine).",
      );
      return;
    }
    if (usesKeys(data.priceRuleSource) && data.priceRuleKeys.length === 0) {
      setSaveError("Sherex price-list pricing needs at least one Price Rule Key.");
      return;
    }

    if (modal.mode === "create") {
      if (!MODEL_ID_RE.test(data.modelId.trim())) {
        setSaveError(
          "Model ID is required — 1–60 chars: letters, digits, or underscore only.",
        );
        return;
      }
      createModel.mutate(toCreateBody(data), {
        onSuccess: () => setModal(null),
        onError: (e) => setSaveError(getApiErrorMessage(e, "Failed to create record.")),
      });
      return;
    }

    if (!modal.id) return;
    updateModel.mutate(
      { id: modal.id, body: toBody(data) },
      {
        onSuccess: () => setModal(null),
        onError: (e) => setSaveError(getApiErrorMessage(e, "Failed to save record.")),
      },
    );
  };

  const isView = modal?.mode === "view";
  const isCreate = modal?.mode === "create";
  const isSaving = updateModel.isPending || createModel.isPending;

  const cols: ColDef<CostModelSummary>[] = [
    { header: "modelId", key: "modelId" },
    { header: "label", render: (i) => i.label || "—" },
    {
      header: "priceRuleSource",
      render: (i) => (
        <span className="font-mono text-blue_grey3 truncate max-w-xs block text-small">{i.priceRuleSource || "—"}</span>
      ),
    },
    { header: "additionalInvestment", render: (i) => dash(i.additionalInvestment) },
    {
      header: "formulas",
      render: (i) => (
        <span
          className={
            i.evalOrderComplete
              ? "text-small-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400"
              : "text-small-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
          }
        >
          {i.evalOrderComplete ? "ordered" : "needs order"}
        </span>
      ),
    },
    {
      header: "Actions",
      render: (i) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(i)} />
          <EditButton onClick={() => openEdit(i)} />
        </div>
      ),
    },
  ];

  const titleMap = {
    edit: "Edit Cost Models record",
    view: "View Cost Models record",
    create: "Add Cost Models record",
  };
  const detailLoading = modal !== null && detailQuery.isLoading;

  return (
    <>
      <PageHeader
        title="Dataset: Cost Models"
        subtitle="Record-level editor for the cost models (svc-10). Add new models or edit constants, formulas and price-rule blocks on existing ones."
        action={
          <AddButton onClick={openCreate} label="Add record" />
        }
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load cost models{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <AdminTable columns={cols} rows={items} keyExtractor={(i) => i.modelId} loading={isLoading} />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-3xl"
        footer={
          isView ? (
            <CancelButton onClick={closeModal} />
          ) : (
            <>
              <CancelButton onClick={closeModal} />
              <SaveButton
                onClick={handleSubmit(onSubmit)}
                label={isSaving ? "Saving…" : isCreate ? "Create record" : "Save record"}
              />
            </>
          )
        }
      >
        {saveError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
            {saveError}
          </div>
        )}

        {!isCreate && (detailLoading || !detail) ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading record…</div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {/* Identity + Label. modelId is the PK: editable on create, read-only after. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isCreate ? (
                <PennField label="Model ID" htmlFor="modelId" required error={errors.modelId?.message}>
                  <PennInput
                    id="modelId"
                    placeholder="e.g. haeger_pem"
                    error={!!errors.modelId}
                    {...register("modelId")}
                  />
                </PennField>
              ) : (
                <ReadOnly label="ID" value={detail?.modelId ?? ""} />
              )}
              <PennField label="Label" htmlFor="label" error={!isView ? errors.label?.message : undefined}>
                <PennInput id="label" disabled={isView} error={!isView && !!errors.label} {...register("label")} />
              </PennField>
            </div>

            {/* Applies To — descriptive metadata. Editable on create, read-only after. */}
            <Section icon={<Settings2 size={14} />} title={isCreate ? "Applies To" : "Applies To (read-only)"}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {isCreate ? (
                  <>
                    <PennField label="Target Type" htmlFor="appliesToTargetType" error={errors.appliesToTargetType?.message}>
                      <PennInput id="appliesToTargetType" placeholder="e.g. machine" error={!!errors.appliesToTargetType} {...register("appliesToTargetType")} />
                    </PennField>
                    <PennField label="Target ID" htmlFor="appliesToTargetId" error={errors.appliesToTargetId?.message}>
                      <PennInput id="appliesToTargetId" error={!!errors.appliesToTargetId} {...register("appliesToTargetId")} />
                    </PennField>
                    <PennField label="Region" htmlFor="appliesToRegion" error={errors.appliesToRegion?.message}>
                      <PennInput id="appliesToRegion" placeholder="e.g. NA" error={!!errors.appliesToRegion} {...register("appliesToRegion")} />
                    </PennField>
                  </>
                ) : (
                  <>
                    <ReadOnly label="Target Type" value={dash(detail?.appliesToTargetType)} />
                    <ReadOnly label="Target ID" value={dash(detail?.appliesToTargetId)} />
                    <ReadOnly label="Region" value={dash(detail?.appliesToRegion)} />
                  </>
                )}
              </div>
            </Section>

            {/* Price Rule — Machine Price Source is editable (functional: it
                selects the engine's pricing strategy). Currency + Additional
                Investment Currency stay read-only (descriptive metadata in the
                MVP — the engine prices in the region's currency, Q-CE-05). */}
            <Section icon={<DollarSign size={14} />} title="Price Rule">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <PennField
                  label="Machine Price Source"
                  htmlFor="priceRuleSource"
                  error={!isView ? errors.priceRuleSource?.message : undefined}
                >
                  <Controller
                    control={control}
                    name="priceRuleSource"
                    render={({ field }) => (
                      <PennDropdown
                        inputId="priceRuleSource"
                        options={PRICE_RULE_SOURCE_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        isDisabled={isView}
                        isSearchable
                        error={!isView && !!errors.priceRuleSource}
                      />
                    )}
                  />
                </PennField>
                {isCreate ? (
                  <PennField label="Currency" htmlFor="currency" error={errors.currency?.message}>
                    <PennInput id="currency" placeholder="e.g. USD" error={!!errors.currency} {...register("currency")} />
                  </PennField>
                ) : (
                  <ReadOnly label="Currency" value={dash(detail?.currency)} />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <PennField
                  label="Additional Investment"
                  htmlFor="additionalInvestment"
                  error={!isView ? errors.additionalInvestment?.message : undefined}
                >
                  <PennInput
                    id="additionalInvestment"
                    disabled={isView}
                    error={!isView && !!errors.additionalInvestment}
                    {...register("additionalInvestment")}
                  />
                </PennField>
                {isCreate ? (
                  <PennField
                    label="Additional Investment Currency"
                    htmlFor="additionalInvestmentCurrency"
                    error={errors.additionalInvestmentCurrency?.message}
                  >
                    <PennInput
                      id="additionalInvestmentCurrency"
                      placeholder="e.g. EUR"
                      error={!!errors.additionalInvestmentCurrency}
                      {...register("additionalInvestmentCurrency")}
                    />
                  </PennField>
                ) : (
                  <ReadOnly
                    label="Additional Investment Currency"
                    value={dash(detail?.additionalInvestmentCurrency)}
                  />
                )}
              </div>
              {!isView && !usesFilter(watchedSource) && !usesKeys(watchedSource) && (
                <p className="mt-3 text-blue_grey3 text-small">
                  This price source is self-contained — it needs no filter or pricing keys.
                  {watchedSource === "mds_a240_base_plus_robot"
                    ? " It uses the base machine price plus the Additional Investment (robot adder) above."
                    : " It sums the configured options."}
                </p>
              )}
            </Section>

            {/* Price Rule Filters — only for avg_base_machine. Each row is a
                category / subCategory pair matched against the catalog's
                machine_options items. "Machine / Machine" means category=Machine
                AND subCategory=Machine (cost-models.json machine_options filter). */}
            {usesFilter(watchedSource) && (
              <Section
                icon={<Filter size={14} />}
                title="Price Rule Filters"
                action={
                  !isView ? (
                    <button
                      type="button"
                      onClick={() => filters.append({ filterCategory: "", filterSubcategory: "" })}
                      className={addBtnClass}
                    >
                      <Plus size={14} /> Add
                    </button>
                  ) : null
                }
              >
                {filters.fields.length === 0 ? (
                  <p className="text-blue_grey4 text-body">No filters.</p>
                ) : (
                  <div className="space-y-2">
                    {filters.fields.map((field, idx) => (
                      <div key={field.id} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-start">
                        <PennInput
                          placeholder="Category (e.g. Machine)"
                          disabled={isView}
                          error={!isView && !!errors.priceRuleFilters?.[idx]?.filterCategory}
                          {...register(`priceRuleFilters.${idx}.filterCategory` as const)}
                        />
                        <PennInput
                          placeholder="Sub-category (e.g. Machine)"
                          disabled={isView}
                          error={!isView && !!errors.priceRuleFilters?.[idx]?.filterSubcategory}
                          {...register(`priceRuleFilters.${idx}.filterSubcategory` as const)}
                        />
                        {!isView ? (
                          <button type="button" onClick={() => filters.remove(idx)} aria-label="Remove filter" className={removeBtnClass}>
                            <Trash2 size={16} />
                          </button>
                        ) : (
                          <span className="w-10" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* Price Rule Keys — only for sherex_price_list_min. */}
            {usesKeys(watchedSource) && (
              <Section
                icon={<KeyRound size={14} />}
                title="Price Rule Keys"
                action={
                  !isView ? (
                    <button
                      type="button"
                      onClick={() => keys.append({ sherexKey: "", sortOrder: "" })}
                      className={addBtnClass}
                    >
                      <Plus size={14} /> Add
                    </button>
                  ) : null
                }
              >
                {keys.fields.length === 0 ? (
                  <p className="text-blue_grey4 text-body">No keys.</p>
                ) : (
                  <div className="space-y-2">
                    {keys.fields.map((field, idx) => (
                      <div key={field.id} className="grid grid-cols-[2fr_1fr_auto] gap-3 items-start">
                        <PennInput
                          placeholder="sherexKey"
                          disabled={isView}
                          error={!isView && !!errors.priceRuleKeys?.[idx]?.sherexKey}
                          {...register(`priceRuleKeys.${idx}.sherexKey` as const)}
                        />
                        <PennInput
                          placeholder="sortOrder"
                          disabled={isView}
                          error={!isView && !!errors.priceRuleKeys?.[idx]?.sortOrder}
                          {...register(`priceRuleKeys.${idx}.sortOrder` as const)}
                        />
                        {!isView ? (
                          <button type="button" onClick={() => keys.remove(idx)} aria-label="Remove key" className={removeBtnClass}>
                            <Trash2 size={16} />
                          </button>
                        ) : (
                          <span className="w-10" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* Constants — dynamic; every key the model actually carries. */}
            <Section
              icon={<SlidersHorizontal size={14} />}
              title="Constants"
              action={
                !isView ? (
                  <button
                    type="button"
                    onClick={() => constants.append({ constantKey: "", value: "" })}
                    className={addBtnClass}
                  >
                    <Plus size={14} /> Add
                  </button>
                ) : null
              }
            >
              {constants.fields.length === 0 ? (
                <p className="text-blue_grey4 text-body">No constants.</p>
              ) : (
                <div className="space-y-2">
                  {constants.fields.map((field, idx) => (
                    <div key={field.id} className="grid grid-cols-[2fr_1fr_auto] gap-3 items-start">
                      <PennInput
                        placeholder="constantKey"
                        disabled={isView}
                        error={!isView && !!errors.constants?.[idx]?.constantKey}
                        {...register(`constants.${idx}.constantKey` as const)}
                      />
                      <PennInput
                        placeholder="value"
                        disabled={isView}
                        error={!isView && !!errors.constants?.[idx]?.value}
                        {...register(`constants.${idx}.value` as const)}
                      />
                      {!isView ? (
                        <button type="button" onClick={() => constants.remove(idx)} aria-label="Remove constant" className={removeBtnClass}>
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <span className="w-10" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Formulas — dynamic, with eval order. */}
            <Section
              icon={<FunctionSquare size={14} />}
              title="Formulas"
              action={
                !isView ? (
                  <button
                    type="button"
                    onClick={() => formulas.append({ formulaKey: "", expression: "", evalOrder: 1 })}
                    className={addBtnClass}
                  >
                    <Plus size={14} /> Add
                  </button>
                ) : null
              }
            >
              <div className="grid grid-cols-[1fr_2fr_5rem_auto] gap-3 mb-2 px-1">
                <p className="text-blue_grey3 text-small-medium">Formula key</p>
                <p className="text-blue_grey3 text-small-medium">Expression</p>
                <p className="text-blue_grey3 text-small-medium">Order</p>
                <span className="w-7" />
              </div>
              {formulas.fields.length === 0 ? (
                <p className="text-blue_grey4 text-body">No formulas.</p>
              ) : (
                <div className="space-y-2">
                  {formulas.fields.map((field, idx) => (
                    <div key={field.id} className="grid grid-cols-[1fr_2fr_5rem_auto] gap-3 items-start">
                      <PennInput
                        placeholder="formulaKey"
                        disabled={isView}
                        error={!isView && !!errors.formulas?.[idx]?.formulaKey}
                        {...register(`formulas.${idx}.formulaKey` as const)}
                      />
                      <PennInput
                        placeholder="expression"
                        disabled={isView}
                        error={!isView && !!errors.formulas?.[idx]?.expression}
                        className="font-mono text-small"
                        {...register(`formulas.${idx}.expression` as const)}
                      />
                      <PennInput
                        type="number"
                        placeholder="0-99"
                        disabled={isView}
                        error={!isView && !!errors.formulas?.[idx]?.evalOrder}
                        {...register(`formulas.${idx}.evalOrder` as const, { valueAsNumber: true })}
                      />
                      {!isView ? (
                        <button type="button" onClick={() => formulas.remove(idx)} aria-label="Remove formula" className={removeBtnClass}>
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <span className="w-10" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </form>
        )}
      </AdminModal>
    </>
  );
};
